import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import React, { useEffect } from 'react';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ValuesCollection } from '@/engine/DataFlow/DataFlow';

export interface IDefaultsResetter {
  doReset: () => void;
}

export const useDefaultsResetter = (
  descriptors: Array<string>
): {
  areDefault: boolean;
  resetter: IDefaultsResetter;
} => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);
  const commandProcessor = useInjection<ICommandProcessor>(CORE_TYPES.CommandProcessor);
  const [areDefault, setAreDefault] = React.useState(true);
  const [resetter, setResetter] = React.useState<IDefaultsResetter>({
    doReset: (): void => {
      return;
    }
  });

  useEffect(() => {
    const defaultsResetter = async (): Promise<void> => {
      const valuesToUndo: ValuesCollection = await Promise.all(
        descriptors.map(async (descriptor) => {
          const value = await planningAlgorithm.getIValueAsync(descriptor);
          return [descriptor, value];
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      commandProcessor.executeAnonymousCommand(
        async (): Promise<undefined> => {
          await planningAlgorithm.resetTuneValuesAsync(descriptors);
        },
        async (): Promise<undefined> => {
          await planningAlgorithm.setTuneValuesAsync(valuesToUndo);
        }
      );
    };

    const onValueChanged = async (tuneValue?: DT.TuneFlag): Promise<void> => {
      if (!tuneValue) {
        throw new Error('tuneValue expected to be valid here');
      }
      if (tuneValue.value == DT.ETuneFlag.USER) {
        setAreDefault(false);
      } else {
        const areDefault = await planningAlgorithm.areTuneValuesDefaultAsync(descriptors);
        setAreDefault(areDefault);
      }
    };

    const subscriptions: DisposablesBasket = new DisposablesBasket();
    for (const descriptor of descriptors) {
      subscriptions.add(
        planningAlgorithm.addOnChangedCallbackFor<DT.TuneFlag>(descriptor + '.flag', (tuneValue?: DT.TuneFlag) => {
          void onValueChanged(tuneValue);
        })
      );
    }

    const job = async (): Promise<void> => {
      const areDefault = await planningAlgorithm.areTuneValuesDefaultAsync(descriptors);
      setAreDefault(areDefault);
    };
    void job();

    setResetter({
      doReset: () => {
        void defaultsResetter();
      }
    });

    return (): void => subscriptions.dispose();
  }, [planningAlgorithm, descriptors, commandProcessor]);

  return { areDefault: areDefault, resetter: resetter };
};
